import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import SucessMessage from './shared/SucessMessage';
import Popover from '../admin/shared/Popover';

function ProductsList() {
  const [products, setProductsData] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");
  const [models, setModelData] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const navigate = useNavigate();

  const gotoAddProducts = () => {
    navigate('/admin/addproducts');
  };

  const handleEditClick = (product) => {
    navigate(`/admin/product/edit/${product.id}`);
  };

  const handleCheckboxChange = (productId) => {
    setSelectedProductIds((prevSelected) =>
      prevSelected.includes(productId)
        ? prevSelected.filter((id) => id !== productId)
        : [...prevSelected, productId]
    );
  };

  const handleRemoveSelectedProducts = () => {
    setShowDeletePopup(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${config.backendUrl}/admin/products`, {
        data: { ids: selectedProductIds },
        headers: {
          token: localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
      });

      setProductsData((prevState) =>
        prevState.filter((product) => !selectedProductIds.includes(product.id))
      );

      setSelectedProductIds([]);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      setShowDeletePopup(false);
    } catch (error) {
      console.error('Error removing products:', error);
    }
  };

  const handleModelChange = (e) => {
    const value = e.target.value;
    setSelectedModel(value);

    if (value === "") {
      setProductsData(allProducts);
    } else {
      setProductsData(allProducts.filter((p) => p.modal === value));
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.post(
          `${config.backendUrl}/admin/products`,
          {},
          {
            headers: {
              token: localStorage.getItem('token'),
              'Content-Type': 'application/json',
            },
          }
        );
        setProductsData(response.data.data);
        setAllProducts(response.data.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    const fetchModels = async () => {
      try {
        const response = await axios.post(
          `${config.backendUrl}/admin/models`,
          {},
          {
            headers: {
              token: localStorage.getItem('token'),
              'Content-Type': 'application/json',
            },
          }
        );
        setModelData(response.data.data);
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };

    fetchProducts();
    fetchModels();
  }, []);

  return (
    <div className="pt-[70px] px-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Products</h2>
          <div className="breadcrumbs text-gray-500 text-sm">
            <Link to="/admin/dashboard" className="hover:underline">Home</Link>
            <span> {'>'} </span> Products
          </div>
        </div>

        <div className="flex gap-3">
          {/* Model Filter Dropdown */}
          <select
            value={selectedModel}
            onChange={handleModelChange}
            className="px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="">All</option>
            {models.map((model, index) => (
              <option key={index} value={model.id}>
                {model.model}
              </option>
            ))}
          </select>

          <button
            onClick={gotoAddProducts}
            className="px-4 py-2 rounded bg-brand text-white hover:bg-gray-700 transition"
          >
            Add Product
          </button>

          {selectedProductIds.length > 0 && (
            <button
              onClick={handleRemoveSelectedProducts}
              className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition"
            >
              Remove Products
            </button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <Popover isVisible={showSuccessMessage}>
          <SucessMessage>
            <h4>Products Removed Successfully</h4>
          </SucessMessage>
        </Popover>
      )}

      {/* Product List */}
      <div className="product-list grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
        {products && products.length > 0 ? (
          products.map((product) => (
            <div
              className="product-listitem relative group border p-2 rounded shadow hover:shadow-lg transition"
              key={product.id}
            >
              <figure className="productimage relative">
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-48 object-cover rounded"
                />
                <div className="prodcuttitle mt-2">
                  <div className="prodcutccode text-gray-500 text-sm">
                    <span>{product.model_name}</span>
                  </div>
                  <h4 className="text-lg font-semibold">{product.name}</h4>

                  {/* Edit Icon */}
                  <div
                    className="edit-icon absolute top-2 right-2 hidden group-hover:block cursor-pointer z-10 text-blue-600 bg-white rounded-full p-1"
                    onClick={() => handleEditClick(product)}
                  >
                    ✏️
                  </div>
                </div>
              </figure>

              {/* Checkbox */}
              <div
                className={`checkbox-icon absolute top-2 left-2 ${
                  selectedProductIds.includes(product.id) ? 'block' : 'hidden'
                } group-hover:block`}
              >
                <input
                  type="checkbox"
                  checked={selectedProductIds.includes(product.id)}
                  onChange={() => handleCheckboxChange(product.id)}
                  className="w-5 h-5"
                />
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-20">
            <img
              src="/images/no-products.svg"
              alt="No products"
              className="mx-auto mb-4 w-32 h-32 opacity-50"
            />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No products found
            </h3>
            <p className="text-gray-500">
              There are no products available for the selected model.
            </p>
            {/* <button
              onClick={gotoAddProducts}
              className="mt-4 px-4 py-2 bg-brand text-white rounded hover:bg-blue-600"
            >
              Add Product
            </button> */}
          </div>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <div className="popup-overlay fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="popup-content bg-white p-5 rounded shadow-lg">
            <h3>Are you sure you want to remove the selected products?</h3>
            <div className="mt-4 flex justify-end gap-3">
              <button
                className="btn-cancel bg-black text-white px-4 py-2 rounded"
                onClick={() => setShowDeletePopup(false)}
              >
                Cancel
              </button>
              <button
                className="btn-delete bg-red-500 text-white px-4 py-2 rounded"
                onClick={handleConfirmDelete}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsList;
